export function insertAtIndex<A>(
  array: readonly A[],
  index: number,
  item: A
): A[] {
  const result = array.slice()
  result.splice(index, 0, item)
  return result
}

export function removeIndex<A>(array: readonly A[], index: number): A[] {
  return array.filter((_, itemIndex) => itemIndex !== index)
}

/**
 * Returns the last element of the Array.
 * Might switch to just using at(-1) at some point but it's only supported by safari starting from 15.4.
 */
export function last<A>(array: readonly A[]): A | undefined {
  return array[array.length - 1]
}

/**
 * Moves the element from fromIndex to toIndex, shifting all other elements naturally.
 */
export function move<A>(array: A[], fromIndex: number, toIndex: number): A[] {
  if (fromIndex === toIndex) return array
  if (isOutOfBounds(array, fromIndex)) return array
  if (isOutOfBounds(array, toIndex)) return array

  const copy = array.slice()
  const from = copy.splice(fromIndex, 1)[0] as A
  copy.splice(toIndex, 0, from)
  return copy
}

function isOutOfBounds(array: unknown[], index: number) {
  return index < 0 || index >= array.length
}

/*
 * Returns a number Array with all numbers from start to stop (inclusive),
 * incremented or decremented by step.
 */
export function range(start: number, stop: number, step: number = 1): number[] {
  return Array.from(
    { length: (stop - start) / step + 1 },
    (_, i) => start + i * step
  )
}

/**
 * Creates a new Array where each sub array (chunk) contains at most 'bySize' elements.
 */
export function chunk<T>(array: readonly T[], bySize: number): T[][] {
  const result: T[][] = []
  let current: T[] = []

  array.forEach((value, i) => {
    if (current.length < bySize) current.push(value)

    if (current.length === bySize || i === array.length - 1) {
      result.push(current)
      current = []
    }
  })

  return result
}

type SortOnField<T> =
  | ((field: T) => string | null | undefined)
  | ((field: T) => number | null | undefined)
/**
 * Sorts the Array in ascending order, using one or more iterators specifying which field to compare.
 * For strings, localCompare is used.
 * The sort is stable if the browser uses a stable sort (all modern engines do)
 */
export function sort<T>(array: readonly T[], ...fields: SortOnField<T>[]): T[] {
  const result = array.slice()

  if (fields.length === 0) {
    fields = [((x) => x) as SortOnField<T>]
  }

  result.sort((a, b) => {
    for (const field of fields) {
      const fieldA = field(a)
      const fieldB = field(b)

      // Non defined values always go at the end
      if (fieldA === null || fieldA === undefined) return 1
      if (fieldB === null || fieldB === undefined) return -1

      if (typeof fieldA === "string" && typeof fieldB === "string") {
        const comparison = fieldA.localeCompare(fieldB)
        if (comparison !== 0) return comparison
      } else if (typeof fieldA === "number" && typeof fieldB === "number") {
        if (fieldA < fieldB) return -1
        if (fieldA > fieldB) return 1
      }
    }

    return 0
  })

  return result
}
